import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Survy, SurvyResponse } from '../../amorg-common';
import { applicationEndpoints } from '../../amorg-common/applicationEndpoints';
import { Button } from '../../designSystem/button/Button';
import { PublicPageWrapper } from '../_pageWrappers/PublicPageWrapper';
import {
    getSurvy,
    getSurvyResponse,
    submitSurvyAnswer
} from '../survy/survyClientApi';
import { InfoChip } from '../../designSystem/Chip';

type PageState = {
    loading: boolean;
    saving: boolean;
    survey: Survy | null;
    response: SurvyResponse | null;
    error: string;
};

const PRACTICE_ANSWER_VALUES = [
    'not-implemented',
    'partially-implemented',
    'implemented',
    'implemented-and-itegrated',
    'best-practice'
] as const;

const PRACTICE_LABELS: Record<typeof PRACTICE_ANSWER_VALUES[number], string> = {
    'not-implemented': 'Ikke implementeret',
    'partially-implemented': 'Delvist implementeret',
    'implemented': 'Etableret',
    'implemented-and-itegrated': 'Implementeret og integreret',
    'best-practice': 'Best practice'
};

export function SurvyQuestionPage(): JSX.Element {
    const params = useParams<{ key: string; surveyId: string; responseId: string; questionIndex: string }>();
    const navigate = useNavigate();
    const [state, setState] = useState<PageState>({
        loading: true,
        saving: false,
        survey: null,
        response: null,
        error: ''
    });
    const [helpOpen, setHelpOpen] = useState(false);

    const questionIndex = Number(params.questionIndex);

    useEffect(() => {
        const load = async () => {
            try {
                const [survey, response] = await Promise.all([
                    getSurvy(params.key, params.surveyId),
                    getSurvyResponse(params.key, params.responseId)
                ]);

                if (!survey || !response) {
                    setState({ loading: false, saving: false, survey, response, error: 'Besvarelsen blev ikke fundet.' });
                    return;
                }

                if (response.surveyId !== params.surveyId) {
                    setState({ loading: false, saving: false, survey, response: null, error: 'Besvarelses-id matcher ikke undersøgelsen.' });
                    return;
                }

                if (response.finalized) {
                    navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_thanks, {
                        key: params.key,
                        surveyId: params.surveyId,
                        responseId: params.responseId
                    }));
                    return;
                }

                if (!Number.isInteger(questionIndex) || questionIndex < 0 || questionIndex >= survey.questions.length) {
                    navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_questions_questionIndex, {
                        key: params.key,
                        surveyId: params.surveyId,
                        responseId: params.responseId,
                        questionIndex: '0'
                    }));
                    return;
                }

                setState({ loading: false, saving: false, survey, response, error: '' });
            }
            catch {
                setState({ loading: false, saving: false, survey: null, response: null, error: 'Kunne ikke indlæse spørgsmål.' });
            }
        };

        load();
    }, [params.key, params.surveyId, params.responseId, params.questionIndex]);

    const selectedAnswer = useMemo(() => {
        if (!state.response)
            return '';

        return state.response.answers.find(a => a.questionIndex === questionIndex)?.answerValue || '';
    }, [state.response, questionIndex]);

    useEffect(() => {
        setHelpOpen(false);
    }, [questionIndex]);

    async function handleSelect(answerValue: typeof PRACTICE_ANSWER_VALUES[number]) {
        setState({ ...state, saving: true, error: '' });
        try {
            await submitSurvyAnswer(params.key, params.responseId, questionIndex, answerValue);
            const response = await getSurvyResponse(params.key, params.responseId);
            if (response && response.surveyId !== params.surveyId) {
                setState({ ...state, saving: false, error: 'Besvarelses-id matcher ikke undersøgelsen.' });
                return;
            }

            setState({ ...state, saving: false, response: response || state.response, error: '' });
        }
        catch {
            setState({ ...state, saving: false, error: 'Kunne ikke gemme svaret.' });
        }
    }

    function goPrevious() {
        if (!state.survey)
            return;

        const prevIndex = Math.max(0, questionIndex - 1);
        navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_questions_questionIndex, {
            key: params.key,
            surveyId: params.surveyId,
            responseId: params.responseId,
            questionIndex: `${prevIndex}`
        }));
    }

    function goNext() {
        if (!state.survey)
            return;

        const nextIndex = questionIndex + 1;
        if (nextIndex >= state.survey.questions.length)
            navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_thanks, {
                key: params.key,
                surveyId: params.surveyId,
                responseId: params.responseId
            }));
        else
            navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_questions_questionIndex, {
                key: params.key,
                surveyId: params.surveyId,
                responseId: params.responseId,
                questionIndex: `${nextIndex}`
            }));
    }

    if (state.loading)
        return <PublicPageWrapper><Container>Indlæser...</Container></PublicPageWrapper>;

    if (!state.survey || !state.response)
        return <PublicPageWrapper><Container><ErrorText>{state.error || 'Besvarelsen blev ikke fundet.'}</ErrorText></Container></PublicPageWrapper>;

    const currentQuestion = state.survey.questions[questionIndex];
    const currentQuestionNumber = questionIndex + 1;
    const progressPct = Math.round((currentQuestionNumber / state.survey.questions.length) * 100);
    const hasHelpText = currentQuestion.question.helpText.trim().length > 0;

    return (
        <PublicPageWrapper>
            <Container>
                <Panel>
                    <Progress>Spørgsmål {currentQuestionNumber} af {state.survey.questions.length}</Progress>
                    <ProgressBarTrack
                        role='progressbar'
                        aria-label='Besvarelsesfremdrift'
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={progressPct}>
                        <ProgressBarFill widthPct={progressPct} />
                    </ProgressBarTrack>
                    <QuestionMain>
                        <QuestionTopRow>
                            <InfoChip variant="outlined-blue">{currentQuestion.label}</InfoChip>
                            {hasHelpText && (
                                <Button variant="text" icon='help'                                
                                    onClick={() => setHelpOpen(!helpOpen)}
                                    text={helpOpen ? 'Skjul hjælp' : 'Hjælp'}
                                />
                            )}
                        </QuestionTopRow>

                        <Question>{currentQuestion.question.text}</Question>

                        {hasHelpText && helpOpen && (
                            <HelpPanel>
                                <HelpTitle>Hjælp til spørgsmålet</HelpTitle>
                                <HelpText>{currentQuestion.question.helpText}</HelpText>
                            </HelpPanel>
                        )}

                        <OptionList>
                            {PRACTICE_ANSWER_VALUES.map(value => (
                                <OptionButton
                                    key={value}
                                    selected={selectedAnswer === value}
                                    disabled={state.saving}
                                    onClick={() => handleSelect(value)}>
                                    {PRACTICE_LABELS[value]}
                                </OptionButton>
                            ))}
                        </OptionList>

                        {state.error && <ErrorText>{state.error}</ErrorText>}

                        <NavRow>
                            <GhostButton onClick={goPrevious} disabled={state.saving || questionIndex === 0}>Forrige</GhostButton>
                            <PrimaryButton onClick={goNext} disabled={state.saving || !selectedAnswer}>Næste</PrimaryButton>
                        </NavRow>
                    </QuestionMain>
                </Panel>
            </Container>
        </PublicPageWrapper>
    );
}

const Container = styled.main`
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 32px 20px;
`;

const Panel = styled.section`
    background: #fff;
    border: 1px solid #d7d7d7;
    border-radius: 12px;
    padding: 24px;
`;

const Progress = styled.p`
    margin: 0 0 8px;
    color: #666;
`;

const ProgressBarTrack = styled.div`
    width: 100%;
    height: 10px;
    border-radius: 999px;
    background: #e8eff7;
    border: 1px solid #d2dfec;
    overflow: hidden;
    margin: 0 0 16px;
`;

const ProgressBarFill = styled.div<{ widthPct: number }>`
    width: ${props => props.widthPct}%;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #1f4c77 0%, #2f6ca1 100%);
    transition: width 180ms ease;
`;

const Question = styled.h1`
    margin: 0 0 16px;
    font-size: 1.5rem;
`;

const QuestionMain = styled.div`
    min-width: 0;
`;

const QuestionTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
`;

const HelpPanel = styled.aside`
    border: 1px solid #dbe8f5;
    border-radius: 10px;
    background: #f6faff;
    padding: 12px;
    margin: 0 0 16px;
`;

const HelpTitle = styled.p`
    margin: 0 0 8px;
    color: #11365b;
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const HelpText = styled.p`
    margin: 0;
    color: #1f2f40;
    line-height: 1.5;
    white-space: pre-wrap;
`;

const OptionList = styled.div`
    display: grid;
    gap: 10px;
`;

const OptionButton = styled.button<{ selected: boolean }>`
    border: 1px solid ${props => props.selected ? '#11365b' : '#c7c7c7'};
    background: ${props => props.selected ? '#eaf3fb' : '#fff'};
    color: #222;
    border-radius: 8px;
    text-align: left;
    padding: 12px 14px;
    cursor: pointer;
`;

const NavRow = styled.div`
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
`;

const PrimaryButton = styled.button`
    border: 0;
    border-radius: 8px;
    background: #11365b;
    color: #fff;
    padding: 12px 18px;
    cursor: pointer;
`;

const GhostButton = styled.button`
    border: 1px solid #11365b;
    border-radius: 8px;
    background: #fff;
    color: #11365b;
    padding: 12px 18px;
    cursor: pointer;
`;

const ErrorText = styled.p`
    color: #b22727;
`;
